import { useContext, useState } from "react"
import { VibratoClientContext } from "./contexts"
import BusinessSearchModal from "./BusinessSearchModal"
import { useNavigate, Link } from "react-router-dom"
import { Building, Search, User, MapPin, Shield, ArrowLeft } from "iconoir-react"
import { countryCodeInfo } from "@/lib/locales"
import { formatUsPhone } from "@/lib/phone"

// Country options based on frontend locales
const COUNTRY_OPTIONS = Object.entries(countryCodeInfo)
  .map(([key, info]) => ({
    code: info.iso, // Use ISO code
    name: info.displayNameClean, // Use clean display name without phone code
    uniqueKey: key // Use the country key as unique identifier
  }))
  .sort((a, b) => a.name.localeCompare(b.name))

export default function BusinessVerificationEdit() {
  const vibratoClient = useContext(VibratoClientContext)
  const navigate = useNavigate()

  const [showSearch, setShowSearch] = useState(false)
  const [selectedBiz, setSelectedBiz] = useState<any>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<any>({})

  // Form state
  const [form, setForm] = useState({
    contact_full_name: "",
    contact_phone: "",
    contact_address_line1: "",
    contact_address_line2: "",
    contact_city: "",
    contact_state: "",
    contact_postal_code: "",
    contact_country: "US",
    verification_notes: "",
    supporting_documents: ""
  })

  const update = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }))

  const submit = async () => {
    if (!vibratoClient || !selectedBiz) return
    try {
      setIsSubmitting(true)
      setErrors({}) // Clear previous errors
      const payload = {
        business_uuid: selectedBiz.uuid,
        ...form
      }
      const resp = await vibratoClient.createBusinessVerificationRequest(payload)
      navigate(`/business/verification/${resp.uuid}`)
    } catch (e: any) {
      console.error("Failed to submit verification request", e)
      if (e.data && typeof e.data === "object") {
        setErrors(e.data)
      } else {
        alert("Failed to submit verification request. Please try again.")
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const hasErrors = Object.keys(errors).length > 0

  // Helper component to display field errors
  const FieldError = ({ fieldName }: { fieldName: string }) => {
    const fieldErrors = errors[fieldName]
    if (!fieldErrors || !Array.isArray(fieldErrors)) return null
    return (
      <div className="text-red-600 text-sm mt-1">
        {fieldErrors.map((error: string, index: number) => (
          <div key={index}>{error}</div>
        ))}
      </div>
    )
  }

  return (
    <div className="h-full w-full p-6 md:p-8 flex flex-col gap-8">
      <BusinessSearchModal
        show={showSearch}
        onClose={() => setShowSearch(false)}
        onSelect={(biz) => setSelectedBiz(biz)}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/business"
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="p-3 bg-purple-100 rounded-xl">
            <Building width="27" className="text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Verify a Business</h1>
            <p className="text-gray-600">Submit a verification request to prove ownership</p>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="rounded-2xl border border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50 p-6">
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 rounded-lg bg-purple-100 p-2">
            <Shield className="h-6 w-6 text-purple-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Verification Process</h3>
        </div>
        <div className="pt-4">
          <p className="mb-3 text-gray-700">
            After submitting your request, you'll receive a unique verification code. Email{" "}
            <span className="break-all font-semibold text-purple-700">business-verifications@getvibrato.com</span> from
            your account email with:
          </p>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-3">
              <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-purple-400"></div>
              <span>Your verification code</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-purple-400"></div>
              <span>Photo of government-issued ID</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-purple-400"></div>
              <span>
                Business ownership proof (utility bill, lease, bank statement with business name, business license, or
                authorization letter)
              </span>
            </li>
          </ul>
        </div>
      </div>

      {/* Business Selection */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <Search className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900">Select Your Business</h2>
          </div>
          <button
            onClick={() => setShowSearch(true)}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
          >
            <Search className="w-5 h-5" />
            {selectedBiz ? "Change Business" : "Search for Business"}
          </button>
        </div>

        {selectedBiz && (
          <div className="p-6">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Building className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 text-lg">{selectedBiz.name}</div>
                  <div className="text-gray-600">
                    {[selectedBiz.city, selectedBiz.state].filter(Boolean).join(", ")}
                  </div>
                  {selectedBiz.phone && (
                    <div className="text-gray-500 text-sm mt-1">{formatUsPhone(selectedBiz.phone)}</div>
                  )}
                  {selectedBiz.address && <div className="text-gray-500 text-sm mt-1">{selectedBiz.address}</div>}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Contact Information */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <User className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900">Contact Information</h2>
          </div>
          <p className="text-gray-600 text-sm">Your contact details for verification purposes</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                required
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                  errors.contact_full_name
                    ? "border-red-300 focus:ring-red-500"
                    : "border-gray-300 focus:ring-purple-500"
                }`}
                placeholder="Enter your full legal name"
                value={form.contact_full_name}
                onChange={(e) => update("contact_full_name", e.target.value)}
              />
              <FieldError fieldName="contact_full_name" />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                required
                type="tel"
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                  errors.contact_phone ? "border-red-300 focus:ring-red-500" : "border-gray-300 focus:ring-purple-500"
                }`}
                placeholder="(555) 123-4567"
                value={form.contact_phone}
                onChange={(e) => update("contact_phone", e.target.value)}
              />
              <FieldError fieldName="contact_phone" />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Country <span className="text-red-500">*</span>
              </label>
              <select
                required
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all bg-white ${
                  errors.contact_country ? "border-red-300 focus:ring-red-500" : "border-gray-300 focus:ring-purple-500"
                }`}
                value={form.contact_country}
                onChange={(e) => update("contact_country", e.target.value)}
              >
                {COUNTRY_OPTIONS.map((country) => (
                  <option key={country.uniqueKey} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
              <FieldError fieldName="contact_country" />
            </div>
          </div>
        </div>
      </div>

      {/* Address Information */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <MapPin className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900">Address Information</h2>
          </div>
          <p className="text-gray-600 text-sm">Your current address for identity verification</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Address Line 1 <span className="text-red-500">*</span>
              </label>
              <input
                required
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                  errors.contact_address_line1
                    ? "border-red-300 focus:ring-red-500"
                    : "border-gray-300 focus:ring-purple-500"
                }`}
                placeholder="123 Main Street"
                value={form.contact_address_line1}
                onChange={(e) => update("contact_address_line1", e.target.value)}
              />
              <FieldError fieldName="contact_address_line1" />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Address Line 2 (Optional)</label>
              <input
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                  errors.contact_address_line2
                    ? "border-red-300 focus:ring-red-500"
                    : "border-gray-300 focus:ring-purple-500"
                }`}
                placeholder="Apt 4B, Suite 200, etc."
                value={form.contact_address_line2}
                onChange={(e) => update("contact_address_line2", e.target.value)}
              />
              <FieldError fieldName="contact_address_line2" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  required
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                    errors.contact_city ? "border-red-300 focus:ring-red-500" : "border-gray-300 focus:ring-purple-500"
                  }`}
                  placeholder="San Francisco"
                  value={form.contact_city}
                  onChange={(e) => update("contact_city", e.target.value)}
                />
                <FieldError fieldName="contact_city" />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  State/Province <span className="text-red-500">*</span>
                </label>
                <input
                  required
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                    errors.contact_state ? "border-red-300 focus:ring-red-500" : "border-gray-300 focus:ring-purple-500"
                  }`}
                  placeholder="CA"
                  value={form.contact_state}
                  onChange={(e) => update("contact_state", e.target.value)}
                />
                <FieldError fieldName="contact_state" />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Postal Code <span className="text-red-500">*</span>
                </label>
                <input
                  required
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                    errors.contact_postal_code
                      ? "border-red-300 focus:ring-red-500"
                      : "border-gray-300 focus:ring-purple-500"
                  }`}
                  placeholder="94102"
                  value={form.contact_postal_code}
                  onChange={(e) => update("contact_postal_code", e.target.value)}
                />
                <FieldError fieldName="contact_postal_code" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Information */}
      {/* <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <Page className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900">Additional Information</h2>
          </div>
          <p className="text-gray-600 text-sm">Help us verify your ownership with additional context</p>
        </div>
        <div className="p-6">
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Verification Notes</label>
              <textarea
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all resize-none ${
                  errors.verification_notes ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-purple-500'
                }`}
                rows={4}
                placeholder="Tell us about your relationship to this business, what documents you have available, or any other context that would help with verification..."
                value={form.verification_notes}
                onChange={(e) => update("verification_notes", e.target.value)}
              />
              <FieldError fieldName="verification_notes" />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Supporting Documents</label>
              <textarea
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all resize-none ${
                  errors.supporting_documents ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-purple-500'
                }`}
                rows={3}
                placeholder="Paste links to supporting documents (Google Drive, Dropbox, etc.) or describe what you'll include in your email..."
                value={form.supporting_documents}
                onChange={(e) => update("supporting_documents", e.target.value)}
              />
              <FieldError fieldName="supporting_documents" />
            </div>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex flex-col items-center gap-4 pb-8">
        <button
          disabled={!selectedBiz || isSubmitting}
          onClick={submit}
          className="px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center gap-3 text-lg"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Submitting Request...
            </>
          ) : (
            <>
              <Shield className="w-6 h-6" />
              Submit Verification Request
            </>
          )}
        </button>

        {hasErrors && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 max-w-md">
            <div className="flex items-center gap-2 text-red-800 font-medium mb-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              Please fix the errors below
            </div>
            <p className="text-red-700 text-sm">
              Check the form fields above for validation errors and correct them before submitting.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
